// src/pages/FreeWorkouts.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import AppNav from '../components/AppNav';
import VideoCard from '../components/VideoCard';
import VideoPlayerModal from '../components/VideoPlayerModal';
import FilterBar from '../components/FilterBar';
import { fetchFreeVideos } from '../services/bunnyStream';
import { fetchSiteContent, fetchUserCompletions, markWorkoutComplete, deleteCompletionById, fetchVideoCompletions, indexCompletions } from '../services/firebase';

function FreeWorkouts({ demoMode }) {
    const navigate = useNavigate();
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeVideo, setActiveVideo] = useState(null);
    const [c, setC] = useState({});
    const [lengthFilter, setLengthFilter] = useState([]);
    const [groupFilter, setGroupFilter] = useState([]);
    const [sortBy, setSortBy] = useState('newest');
    const [completedIds, setCompletedIds] = useState(new Set());
    const [perVideoCompletions, setPerVideoCompletions] = useState({});
    const [completedFilter, setCompletedFilter] = useState(null);

    let userId = '';
    if (!demoMode) {
        try { const { user } = useUser(); userId = user?.id || ''; } catch (e) { }
    } else { userId = 'demo'; }

    useEffect(() => {
        async function load() {
            setLoading(true);
            const [driveVids, content, completions] = await Promise.all([
                fetchFreeVideos(), fetchSiteContent(), fetchUserCompletions(userId),
            ]);
            setVideos(driveVids && driveVids.length > 0 ? driveVids : []);
            setC(content || {});
            const { completedIds: ids, perVideo } = indexCompletions((completions || []).filter((comp) => !comp.programId));
            setCompletedIds(ids);
            setPerVideoCompletions(perVideo);
            setLoading(false);
        }
        load();
    }, []);

    const handleLogComplete = async (video) => {
        const occurrenceKey = `${video.id}_manual_${Date.now()}`;
        const rec = await markWorkoutComplete(userId, video.id, video.title, null, { occurrenceKey, source: 'manual' });
        if (rec) {
            setCompletedIds((prev) => new Set(prev).add(video.id));
            setPerVideoCompletions((prev) => {
                const cur = prev[video.id];
                const last = rec.completedAt || new Date().toISOString();
                return { ...prev, [video.id]: { count: (cur?.count || 0) + 1, lastCompletedAt: last } };
            });
        }
        return rec;
    };

    const handleUndoComplete = async (video, completionId) => {
        const ok = await deleteCompletionById(userId, completionId);
        if (!ok) return false;
        const remaining = (perVideoCompletions[video.id]?.count || 1) - 1;
        setPerVideoCompletions((prev) => {
            const cur = prev[video.id];
            if (!cur) return prev;
            const count = Math.max(0, (cur.count || 1) - 1);
            const next = { ...prev };
            if (count === 0) { delete next[video.id]; } else { next[video.id] = { ...cur, count }; }
            return next;
        });
        if (remaining <= 0) {
            setCompletedIds((prev) => { const n = new Set(prev); n.delete(video.id); return n; });
        }
        return true;
    };

    // Lazy loader for the "Your history" remover. Resolves the id-backed session
    // list just-in-time; free workouts are programId-less, so scope = null.
    const handleLoadHistory = (video) => {
        if (!userId || !video?.id) return Promise.resolve([]);
        return fetchVideoCompletions(userId, video.id, null);
    };

    const completionCopy = {
        logLabel: c?.completionLogLabel,
        logAgainLabel: c?.completionLogAgainLabel,
        loggedLabel: c?.completionLoggedLabel,
        undoLabel: c?.completionUndoLabel,
        removeConfirmLabel: c?.completionRemoveConfirmLabel,
        removeKeepLabel: c?.completionRemoveKeepLabel,
        historyDisclosureLabel: c?.completionHistoryDisclosureLabel,
        historyRemoveLabel: c?.completionHistoryRemoveLabel,
    };

    const availableDurations = [...new Set(videos.map((v) => v.length))].sort((a, b) => a - b);

    let orderedVideos = videos;
    const freeVideoOrder = c?.freeVideoOrder;
    if (freeVideoOrder && freeVideoOrder.length > 0) {
        const ordered = [];
        freeVideoOrder.forEach((id) => { const v = videos.find((vid) => vid.id === id); if (v) ordered.push(v); });
        videos.forEach((v) => { if (!freeVideoOrder.includes(v.id)) ordered.push(v); });
        orderedVideos = ordered;
    }

    const filtered = orderedVideos
        .filter((v) => lengthFilter.length === 0 || lengthFilter.includes(v.length))
        .filter((v) => groupFilter.length === 0 || groupFilter.some((g) => v.groups.includes(g)))
        .filter((v) => {
            if (completedFilter === 'completed') return completedIds.has(v.id);
            return true;
        })
        .sort((a, b) => {
            // Push completed videos to the bottom (unless filtering by completed)
            if (!completedFilter) {
                const aComp = completedIds.has(a.id) ? 1 : 0;
                const bComp = completedIds.has(b.id) ? 1 : 0;
                if (aComp !== bComp) return aComp - bComp;
            }
            // Sort by selected option — always respect the user's choice
            if (sortBy === 'newest') return (b.createdTime || '').localeCompare(a.createdTime || '');
            if (sortBy === 'length-asc') return a.length - b.length;
            if (sortBy === 'length-desc') return b.length - a.length;
            return a.title.localeCompare(b.title);
        });

    return (
        <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <AppNav demoMode={demoMode} />
            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
                <div style={{ marginBottom: 40 }}>
                    <h1 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(28px, 4vw, 36px)', fontStyle: 'italic', marginBottom: 8, color: 'var(--mom-charcoal)' }}>{c.freeWorkoutsTitle || 'Free Workouts'}</h1>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: 'var(--mom-warm-gray)' }}>
                        {loading ? '\u00A0' : (c.freeWorkoutsSubtitle || `${videos.length} free workout${videos.length !== 1 ? 's' : ''} — no program required.`)}
                    </p>
                </div>

                {loading ? (
                    <div>
                        <div className="mom-skeleton" style={{ height: 80, borderRadius: 20, marginBottom: 32 }} />
                        <div className="mom-video-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20 }}>
                            {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="mom-skeleton" style={{ height: 320, borderRadius: 20 }} />)}
                        </div>
                    </div>
                ) : videos.length === 0 ? (
                    <div className="mom-card" style={{ padding: '60px 32px', textAlign: 'center', borderRadius: 20 }}>
                        <p style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8, position: 'relative', zIndex: 2 }}>{c.noFreeWorkoutsMessage || 'No free workouts yet'}</p>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', position: 'relative', zIndex: 2 }}>{c.emptyWorkoutsSubtitle || 'Check back soon — new workouts are on the way!'}</p>
                    </div>
                ) : (
                    <>
                        <FilterBar lengthFilter={lengthFilter} setLengthFilter={setLengthFilter} groupFilter={groupFilter} setGroupFilter={setGroupFilter} sortBy={sortBy} setSortBy={setSortBy} availableDurations={availableDurations}
                            completedFilter={completedFilter} setCompletedFilter={setCompletedFilter} completedCount={completedIds.size} />

                        <div className="mom-video-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 20 }}>
                            {filtered.map((v, i) => (
                                <VideoCard key={v.id} video={v} onClick={setActiveVideo} animationDelay={Math.min(i * 0.05, 0.5)} vertical
                                    isCompleted={completedIds.has(v.id)}
                                    completionCount={perVideoCompletions[v.id]?.count || 0}
                                    lastCompletedAt={perVideoCompletions[v.id]?.lastCompletedAt || null}
                                    onLogComplete={handleLogComplete} onUndoComplete={handleUndoComplete} onLoadHistory={handleLoadHistory} completionCopy={completionCopy} />
                            ))}
                        </div>

                        {filtered.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '80px 24px' }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 18, color: 'var(--mom-warm-gray)' }}>No workouts match your filters.</p>
                            </div>
                        )}
                    </>
                )}
            </div>
            {activeVideo && <VideoPlayerModal video={activeVideo} onClose={() => setActiveVideo(null)} />}
        </div>
    );
}

export default FreeWorkouts;